import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { CreateSiteSchema, wpConfigLine, type VerifyResult } from '@ametica/shared';
import { useCreateSite, useNewSiteToken, useVerifySite } from '../../api/sites';
import { ApiError } from '../../api/client';
import { Icon } from '../../components/Icon';
import { copyText, useToast } from '../../components/Toast';

/**
 * Pridanie stránky podľa design/AddSite.dc.html.
 * Token sa buď vygeneruje (server ho neukladá), alebo vloží vlastný — MU-plugin
 * sa nasadzuje skôr, než stránka vznikne v dashboarde. Overenie beží PRED uložením.
 */
export function AddSiteDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [ownToken, setOwnToken] = useState(false);
  const [token, setToken] = useState('');
  const [verification, setVerification] = useState<VerifyResult | null>(null);

  const generated = useNewSiteToken(!ownToken);
  const verify = useVerifySite();
  const create = useCreateSite();

  const effectiveToken = ownToken ? token.trim() : (generated.data?.token ?? '');
  const configLine = useMemo(() => (effectiveToken ? wpConfigLine(effectiveToken) : ''), [effectiveToken]);

  // zmena URL alebo tokenu zneplatní predchádzajúce overenie
  useEffect(() => setVerification(null), [url, effectiveToken]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const validation = CreateSiteSchema.safeParse({ name, url, token: effectiveToken });
  const fieldError = (field: 'name' | 'url' | 'token') =>
    validation.success ? null : validation.error.issues.find((i) => i.path[0] === field)?.message ?? null;

  async function runVerify() {
    if (!validation.success) return;
    try {
      setVerification(await verify.mutateAsync({ url: validation.data.url, token: validation.data.token }));
    } catch (e) {
      setVerification({ ok: false, reason: 'http_error', message: e instanceof ApiError ? e.message : 'Overenie zlyhalo.' });
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!validation.success) return;
    await create.mutateAsync(validation.data);
    toast('Stránka pridaná');
    onClose();
  }

  const canSubmit = validation.success && !create.isPending;

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby="add-site-title" onSubmit={submit} noValidate>
        <div className="flex items-center justify-between border-b border-line px-6 py-5">
          <span id="add-site-title" className="text-[15.5px] font-semibold">Pridať stránku</span>
          <button type="button" onClick={onClose} aria-label="Zavrieť" className="grid h-7 w-7 place-items-center rounded-chip text-faint hover:text-ink">
            <Icon name="x" size={17} />
          </button>
        </div>

        <div className="flex flex-col gap-5 p-6">
          <label className="field">
            <span>Názov</span>
            <span className="fbox"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Klient C" autoFocus /></span>
            {name && fieldError('name') && <span className="hint text-sev-critical">{fieldError('name')}</span>}
          </label>

          <label className="field">
            <span>URL stránky</span>
            <span className="fbox"><input className="mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://klient-c.sk" inputMode="url" /></span>
            <span className="hint">{url && fieldError('url') ? <span className="text-sev-critical">{fieldError('url')}</span> : 'Musí byť verejne dostupná cez HTTPS. Interné adresy sú odmietnuté.'}</span>
          </label>

          <div className="field">
            <span className="flex items-center justify-between">
              <span>Site token</span>
              <label className="flex cursor-pointer items-center gap-2 text-[11.5px] font-normal text-faint">
                <input type="checkbox" checked={ownToken} onChange={(e) => setOwnToken(e.target.checked)} />
                plugin už beží, vložím vlastný
              </label>
            </span>
            {ownToken ? (
              <span className="fbox"><input className="mono" value={token} onChange={(e) => setToken(e.target.value)} placeholder="token z wp-config.php (≥ 32 znakov)" /></span>
            ) : (
              <span className="fbox">
                <span className="mono truncate">{generated.data?.token ?? (generated.isError ? 'token sa nepodarilo vygenerovať' : 'generujem…')}</span>
                <CopyButton text={generated.data?.token} label="Token skopírovaný" />
              </span>
            )}
            <span className="hint">
              {ownToken
                ? (token && fieldError('token')) || 'Rovnaký reťazec, aký je v AMETICA_SECURITY_TOKEN na stránke.'
                : 'Vygenerovaný teraz. Uloží sa šifrovaný a už ho znovu nezobrazíme.'}
            </span>
          </div>

          {configLine && (
            <div className="field">
              <span>Vlož do <span className="mono">wp-config.php</span></span>
              <span className="fbox items-start py-2.5">
                <code className="mono break-all text-[11.5px] leading-[1.65] text-dim">
                  define('<span className="text-brand">AMETICA_SECURITY_TOKEN</span>', '<span className="text-ink">{effectiveToken}</span>');
                </code>
                <CopyButton text={configLine} label="Riadok skopírovaný" />
              </span>
              <span className="hint">Potom nahraj <span className="mono">ametica-watcher.php</span> do <span className="mono">wp-content/mu-plugins/</span>.</span>
            </div>
          )}

          {verification && <VerificationNote result={verification} />}

          <div className="flex flex-wrap items-center justify-between gap-2.5 pt-1">
            <button type="button" className="btn btn-g" onClick={runVerify} disabled={!validation.success || verify.isPending}>
              {verify.isPending ? 'Overujem…' : 'Otestovať spojenie'}
            </button>
            <div className="flex gap-2.5">
              <button type="button" className="btn btn-g" onClick={onClose}>Zrušiť</button>
              <button type="submit" className="btn btn-p" disabled={!canSubmit}>
                {create.isPending ? 'Ukladám…' : 'Pridať stránku'}
              </button>
            </div>
          </div>
          {create.error && <p role="alert" className="hint text-sev-critical">{create.error.message}</p>}
        </div>
      </form>
    </div>
  );
}

function CopyButton({ text, label }: { text?: string; label: string }) {
  const toast = useToast();
  return (
    <button
      type="button"
      className="btn btn-g h-[27px] shrink-0 text-[11.5px]"
      disabled={!text}
      onClick={async () => { if (text && (await copyText(text))) toast(label); }}
    >
      <Icon name="copy" size={12} />
      Kopírovať
    </button>
  );
}

const REASON_HINT: Record<Exclude<VerifyResult, { ok: true }>['reason'], string> = {
  blocked_url: 'URL mieri do internej siete alebo na rezervovaný rozsah.',
  unreachable: 'Stránka sa neozvala.',
  timeout: 'Stránka neodpovedala včas.',
  too_many_redirects: 'Priveľa presmerovaní.',
  unauthorized: 'Zlý token.',
  missing_plugin: 'Plugin neodpovedá.',
  cached: 'Cache pred API.',
  invalid_payload: 'Nesprávny tvar odpovede.',
  http_error: 'HTTP chyba.',
};

function VerificationNote({ result }: { result: VerifyResult }) {
  if (result.ok) {
    return (
      <div className="note border-ok/25 bg-ok/10">
        <Icon name="check" size={16} className="shrink-0 text-ok" />
        <span className="text-[12.5px]">
          Spojenie funguje — WordPress {result.wpVersion}, PHP {result.phpVersion}, {result.plugins} pluginov, {result.themes} tém
          {!result.inventoryComplete && <span className="text-sev-high"> · multisite: inventár je neúplný</span>}
        </span>
      </div>
    );
  }
  return (
    <div className="note border-sev-critical/30 bg-sev-critical/10" role="alert">
      <Icon name="warn" size={16} className="mt-px shrink-0 text-sev-critical" />
      <div>
        <div className="text-[12.5px] font-semibold">{REASON_HINT[result.reason]}{result.status ? ` (HTTP ${result.status})` : ''}</div>
        <div className="hint">{result.message}</div>
      </div>
    </div>
  );
}
